import { createRoot } from 'react-dom/client';
import { configureStore, combineReducers } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';
import * as sagas from 'redux-saga/effects';
import { Provider, connect } from 'react-redux';

export default function dva() {
  // 1. Create saga
  const sagaMiddleware = createSagaMiddleware();
  const promiseMiddleware = () => (next) => (action) => {
    const isEffect = (type) => {
      if (!type || typeof type !== 'string') return false;
      const [namespace, reducerKey] = type.split('/');
      const model = app._models.find((m) => m.namespace === namespace);
      if (model && model.effects && model.effects[reducerKey]) {
        return true;
      }

      return false;
    };

    if (isEffect(action.type)) {
      return new Promise((resolve, reject) => {
        next({
          _resolve: resolve,
          _reject: reject,
          ...action,
        });
      });
    } else {
      return next(action);
    }
  };

  const app = {
    _models: [],
    _router: null,
    _store: null,
    _root: null,
    // Register a model
    model(m) {
      m.effects = {
        ...(m.effects || {}),
        *setState({ payload }, { put }) {
          yield put({ type: '_setState', payload });
        },
      };
      m.reducers = {
        _setState(state, { payload }) {
          return { ...state, ...payload };
        },
      };

      this._models.push(m);
    },
    // Register router
    router(r) {
      this._router = r;
    },
    // Start the app and render React root
    start(container) {
      // 2. Prepare reducers and sagas
      const reducers = {};

      this._models.forEach((m) => {
        // Reducer for model namespace
        reducers[m.namespace] = (state = m.state, action) => {
          const { type } = action;
          const [namespace, reducerKey] = type.split('/');

          if (namespace === m.namespace && m.reducers && m.reducers[reducerKey]) {
            return m.reducers[reducerKey](state, action);
          }
          return state;
        };
      });

      // 3. Configure Redux store using Redux Toolkit
      this._store = configureStore({
        reducer: combineReducers(reducers),
        middleware: (getDefaultMiddleware) =>
          getDefaultMiddleware({
            serializableCheck: false,
            thunk: false,
            immutableCheck: false,
          }).concat(
            promiseMiddleware, // Must be the first one
            sagaMiddleware,
          ),
      });

      // 4.  Prepare and enhance Effects, and run all sagas
      const rootSaga = function* () {
        app._models.forEach((m) => {
          const effects = m.effects || {};
          Object.keys(effects).forEach((key) => {
            sagaMiddleware.run(function* (action) {
              yield sagas.takeEvery(`${m.namespace}/${key}`, function* (action) {
                try {
                  const put = (a) => {
                    const type = a.type.includes('/') ? a.type : `${m.namespace}/${a.type}`;
                    return sagas.put({ ...a, type });
                  };
                  put.resolve = (a) => {
                    const type = a.type.includes('/') ? a.type : `${m.namespace}/${a.type}`;
                    return sagas.putResolve({ ...a, type });
                  };

                  const effectTools = {
                    ...sagas,
                    put,
                    select: (s) => sagas.select(s ? s : (state) => state[m.namespace]),
                  };
                  const result = yield m.effects[key](action, effectTools);

                  action._resolve && action._resolve(result);
                } catch (e) {
                  action._reject && action._reject(e);
                  console.error(`Dva Effect Error: ${m.namespace}/${key}`, e);
                }
              });
            });
          });
        });
      };
      sagaMiddleware.run(rootSaga);

      // 5. Execute subscriptions
      this._models.forEach((m) => {
        const subs = m.subscriptions || {};
        Object.values(subs).forEach((sub) =>
          sub({
            dispatch: (a) =>
              this._store.dispatch({
                ...a,
                type: a.type.includes('/') ? a.type : `${m.namespace}/${a.type}`,
              }),
          }),
        );
      });

      // 6. Render App using React createRoot
      const rootElement =
        typeof container === 'string' ? document.querySelector(container) : container;
      this._root = createRoot(rootElement);

      this._root.render(<Provider store={this._store}>{this._router}</Provider>);
    },
  };

  return app;
}

export { connect };
